/**
 * What cooking does to a food, and what it does not.
 *
 * The thing nearly every tracker gets wrong: cooking does not change calories.
 * A raw chicken breast and the same breast grilled hold the same protein and
 * the same energy — what left was water. So weighing raw is not an
 * approximation to be corrected, it is the more accurate measurement, and the
 * only real arithmetic is the fat that went into the pan.
 *
 * Two numbers per method, then. `yieldFactor` is what the food weighs
 * afterwards, which matters when only half the pan gets eaten. `addedFatPer100g`
 * is the oil that ends up in the food, and it is the one that moves the total.
 */

import type { Per100g, RecipeIngredientInput } from './macros.js'
import { round } from './macros.js'

export type CookingMethod = 'raw' | 'boiled' | 'steamed' | 'oven' | 'grilled' | 'pan' | 'deep_fried'

export interface CookingProfile {
  /** Cooked weight ÷ raw weight. Water, not energy. */
  yieldFactor: number
  /** Grams of oil retained per 100 g of raw food. */
  addedFatPer100g: number
}

/**
 * Yields are the mid-points of the USDA retention tables for lean meat, which
 * is what people weigh raw and then argue about. Fat retention is deliberately
 * conservative: most of what goes in a pan stays in the pan, and a tracker that
 * over-counts oil teaches people to stop logging it.
 */
export const COOKING_METHODS: Readonly<Record<CookingMethod, CookingProfile>> = {
  raw: { yieldFactor: 1, addedFatPer100g: 0 },
  boiled: { yieldFactor: 0.9, addedFatPer100g: 0 },
  steamed: { yieldFactor: 0.88, addedFatPer100g: 0 },
  oven: { yieldFactor: 0.75, addedFatPer100g: 1 },
  grilled: { yieldFactor: 0.72, addedFatPer100g: 1 },
  pan: { yieldFactor: 0.75, addedFatPer100g: 4 },
  deep_fried: { yieldFactor: 0.7, addedFatPer100g: 9 },
}

export const COOKING_ORDER: readonly CookingMethod[] = [
  'raw',
  'boiled',
  'steamed',
  'oven',
  'grilled',
  'pan',
  'deep_fried',
]

/** Rapeseed or olive — the two in every Lithuanian kitchen, and identical here. */
/**
 * Rapeseed or olive, which is what is in the pan here.
 *
 * The four secondary nutrients are stated as zeros rather than left out on
 * purpose: an absent value means "unknown", and one unknown row nulls the whole
 * day's sugar, salt and fibre totals. Oil has none of the three, and its
 * saturated fat is a real ~8 g/100 g that belongs against the day's ceiling —
 * frying is exactly where saturated fat comes from.
 */
const OIL_PER_100G: Per100g = {
  kcal: 900,
  protein: 0,
  carbs: 0,
  fat: 100,
  sugars: 0,
  satFat: 8,
  fiber: 0,
  salt: 0,
}

export interface CookedResult {
  /** Totals for the whole raw amount, once the oil is counted. */
  kcal: number
  protein: number
  carbs: number
  fat: number
  /** What comes out of the pan, for portioning what is left. */
  cookedWeightG: number
  /** Energy the oil alone contributed, so the UI can show its working. */
  addedFatKcal: number
  /** Grams of oil, so the diary can carry it as its own line rather than
   *  reconstructing it from the energy and hoping the constants agree. */
  addedOilG: number
}

/**
 * Cook a weighed raw amount.
 *
 * Everything scales off the raw weight, never the cooked one: cooked weight is
 * an estimate and raw weight is a measurement, and dividing by an estimate to
 * multiply by it again only launders the error.
 */
export function applyCooking(
  per100gRaw: Per100g,
  rawWeightG: number,
  method: CookingMethod,
): CookedResult {
  const profile = COOKING_METHODS[method]
  const share = rawWeightG / 100
  const oilG = profile.addedFatPer100g * share

  return {
    kcal: round(per100gRaw.kcal * share + (OIL_PER_100G.kcal * oilG) / 100, 1),
    protein: round(per100gRaw.protein * share, 1),
    carbs: round(per100gRaw.carbs * share, 1),
    fat: round(per100gRaw.fat * share + oilG, 1),
    cookedWeightG: round(rawWeightG * profile.yieldFactor + oilG, 0),
    addedFatKcal: round((OIL_PER_100G.kcal * oilG) / 100, 0),
    addedOilG: round(oilG, 1),
  }
}

/** The oil as a diary row: 100 g of oil is 900 kcal and nothing else. */
export const COOKING_OIL_PER_100G: Per100g = OIL_PER_100G

/** Grams of oil one method leaves in one weighed amount of food. */
export function retainedOilG(rawWeightG: number, method: CookingMethod | null): number {
  if (method == null) return 0
  return round((COOKING_METHODS[method].addedFatPer100g * rawWeightG) / 100, 1)
}

/** One line of a recipe: what went in, and what it met on the way. */
export interface CookedIngredient {
  per100g: Per100g
  rawWeightG: number
  cooking?: CookingMethod | null
}

/**
 * The ingredient list a dish is actually made of.
 *
 * A recipe is one pot but rarely one pan — the egg is boiled while the ham is
 * fried — and the only part of cooking that changes what the dish is worth is
 * the oil left behind, which is why each line carries its own method. The oil
 * is appended as one more ingredient rather than folded into the ones that
 * absorbed it: it is a real 13 g of a real substance, the finished dish weighs
 * more because of it, and every total downstream — macros, raw weight, yield —
 * then counts it exactly once without knowing anything about pans.
 *
 * Returns the list unchanged when nothing was cooked in fat, so a salad's
 * arithmetic is untouched.
 */
export function withCookingOil(ingredients: readonly CookedIngredient[]): RecipeIngredientInput[] {
  const oilG = round(
    ingredients.reduce((sum, i) => sum + retainedOilG(i.rawWeightG, i.cooking ?? null), 0),
    1,
  )
  const lines = ingredients.map((i) => ({ per100g: i.per100g, rawWeightG: i.rawWeightG }))
  return oilG > 0 ? [...lines, { per100g: OIL_PER_100G, rawWeightG: oilG }] : lines
}

/**
 * Foods likely enough to meet heat that the picker should open on its options.
 *
 * This no longer decides whether the question is asked — it is asked on every
 * weighed food now, because a list of words will always miss something ("broilerio
 * file", "Rimi krūtinėlė", any own-brand packet named after the shop) and a
 * control you cannot find is worse than one that is simply always there.
 *
 * What it still decides is whether the row opens expanded, which is a matter of
 * saving one tap on a chicken breast. Being wrong is therefore cheap in both
 * directions, and the list is written wide rather than careful.
 */
const COOKABLE = new RegExp(
  [
    // Meat and poultry, including the cuts and the words shops actually print.
    'vi[sš]tien|kalakut|ant[ie]n|[zž]\\u0105sien|jautien|ver[sš]ien|kiaulien|aviena|[eė]rien|triu[sš]ien',
    'broiler|paukštien|paukstien|m[eė]sa|m[eė]sos|far[sš]|malt[aoų]|kapot',
    'kepsn|steik|file|fil[eė]|kr[uū]tin[eė]l|[sš]launel|sparnel|kumpi|nugarin|i[sš]pjov|men[cč]iuk',
    'lašinia|lasinia|šonin|sonin|kaulel|mentė|mente|sprandin|jautienos|kiaulienos|dešrel|desrel|dešra|desra',
    // Fish and seafood.
    'la[sš]i[sš]|men[kk]|silk|tuna|skumbr|upėtak|upetak|ungur|ster[kk]|karp|otas|plek[sš]n',
    '[zž]uv|jūros gėrybi|juros gerybi|krevet|kalmar|midij|austr|kranklia|v[eė][zž]',
    // Starches.
    'bulv|ry[zž]i|grik|makaron|spagle|spaget|penne|lakšt|lakst|kuskus|bulgur|kino[ja]|kruop|manų',
    'l[eę][sš]i|pupel|avin[zž]|sojos|polent|nudl',
    // Eggs and plant proteins.
    'kiau[sš]in|tofu|tempeh|seitan|sojos gabal',
    // Vegetables and mushrooms — everything that goes in a pan or an oven.
    'dar[zž]ov|brokol|[zž]iedin|kop[uū]st|mork|cukin|moli[uū]g|bakla[zž]|pipir|paprik',
    'pomidor|svog[uū]n|[cč]esnak|por[ao]|salier|burok|rop[eė]|pastarnok|[sš]parag|[zž]irne',
    'grybai|grybų|grybu|pievagryb|baravyk|voveru[sš]|šampinjon|sampinjon',
    // English, for the Open Food Facts and USDA half of the catalogue.
    'chicken|turkey|duck|goose|beef|veal|pork|lamb|mutton|mince|ground meat|bacon|sausage',
    'steak|fillet|filet|breast|thigh|wing|loin|tenderloin|brisket|ribs?',
    'salmon|cod|herring|mackerel|trout|tuna|shrimp|prawn|squid|mussel|scallop|fish',
    'potato|rice|pasta|noodle|spaghetti|macaroni|couscous|bulgur|quinoa|lentil|bean|chickpea',
    'egg|tofu|tempeh|vegetable|broccoli|cauliflower|cabbage|carrot|courgette|zucchini|aubergine',
    'eggplant|pepper|onion|garlic|leek|celery|beet|asparagus|pea|mushroom|champignon',
  ].join('|'),
  'i',
)

/**
 * Whether the cooking row should open on its chips.
 *
 * Named for what it means rather than for what it gates, because what it gates
 * has changed: the picker itself is unconditional on anything weighed in grams.
 */
export function looksCookable(name: string): boolean {
  return COOKABLE.test(name)
}
